// @oasis-permission-inherits: /jerboa (jerboa:view)
import { Link, createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { RenderingStatusBadge } from "~/modules/jerboa/components/rendering-status-badge";
import { getJerboaRenderingResultQuery } from "~/modules/jerboa/queries";

function formatBytes(bytes: number) {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export const Route = createFileRoute("/_dash/jerboa/rendering-results/$resultId")({
  validateSearch: z.object({
    page: z.number().optional(),
    status: z.enum(["SUCCESS", "RENDER_FAILED"]).optional(),
    projectType: z.enum(["captions-only", "ai-edit"]).optional(),
  }),
  component: RouteComponent,
  async loader({ params, context }) {
    const { queryClient } = context;
    await queryClient.prefetchQuery(
      getJerboaRenderingResultQuery.queryOptions({
        pathParams: { resultId: params.resultId },
      })
    );
  },
  pendingComponent: PendingComponent,
});

function PendingComponent() {
  const { page, status, projectType } = Route.useSearch();
  return (
    <div className="space-y-6">
      <Link
        to="/jerboa/rendering-results"
        search={{ page, status, projectType }}
        className="text-sm text-muted-foreground hover:underline"
      >
        &larr; Back to Rendering Results
      </Link>
      <Skeleton className="h-10 w-96 rounded-md" />
      <Skeleton className="h-48 rounded-md" />
      <Skeleton className="h-48 rounded-md" />
    </div>
  );
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
  toast.success("Copied to clipboard");
}

function MetricCard({
  label,
  value,
}: {
  label: string;
  value: string | number | null | undefined;
}) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium mt-1">{value ?? "-"}</div>
    </div>
  );
}

function parseJsonSafe(json: string | null | undefined): unknown {
  if (!json) {
    return null;
  }
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function RouteComponent() {
  const { resultId } = Route.useParams();
  const { page, status, projectType } = Route.useSearch();

  const { data } = getJerboaRenderingResultQuery.useSuspenseQuery({
    pathParams: { resultId },
  });

  const result = data.result;

  return (
    <div className="space-y-6">
      <Link
        to="/jerboa/rendering-results"
        search={{ page, status, projectType }}
        className="text-sm text-muted-foreground hover:underline"
      >
        &larr; Back to Rendering Results
      </Link>

      <Card>
        <CardHeader>
          <CardTitle>Overview</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Status:</span>
            <RenderingStatusBadge status={result.status} />
          </div>
          {result.projectType && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Project Type:</span>
              <Badge variant="outline">{result.projectType}</Badge>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Created At:</span>
            <span className="text-sm">{new Date(result.createdAt).toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">ID:</span>
            <code className="text-sm">{result.id}</code>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => copyToClipboard(result.id)}
            >
              Copy
            </Button>
          </div>
          {result.captionsProjectId && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Captions Project ID:</span>
              <code className="text-sm">{result.captionsProjectId}</code>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => copyToClipboard(result.captionsProjectId!)}
              >
                Copy
              </Button>
            </div>
          )}
          {result.projectProtoUri && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Project Proto URI:</span>
              <code className="text-sm truncate max-w-lg">{result.projectProtoUri}</code>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs shrink-0"
                onClick={() => copyToClipboard(result.projectProtoUri!)}
              >
                Copy
              </Button>
            </div>
          )}
          {result.error && (
            <div>
              <span className="text-sm text-muted-foreground">Error:</span>
              <pre className="mt-1 rounded-md bg-destructive/10 p-3 text-sm text-destructive overflow-auto whitespace-pre-wrap break-words">
                {result.error}
              </pre>
            </div>
          )}
          {result.exportSettingsJson && (
            <div>
              <span className="text-sm text-muted-foreground">Export Settings:</span>
              <pre className="mt-1 max-h-64 overflow-auto rounded-md bg-muted p-3 text-xs">
                {JSON.stringify(parseJsonSafe(result.exportSettingsJson), null, 2)}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>

      {result.videoAsset?.url && (
        <Card>
          <CardHeader>
            <CardTitle>Video</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center gap-2">
              <video src={result.videoAsset.url} controls className="max-h-[480px] rounded-md" />
              {result.videoAsset.sizeBytes != null && (
                <span className="text-xs text-muted-foreground">
                  {formatBytes(result.videoAsset.sizeBytes)}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Render Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <MetricCard
              label="Render Duration"
              value={
                result.renderStats?.renderSeconds != null
                  ? `${result.renderStats.renderSeconds.toFixed(1)}s`
                  : null
              }
            />
            <MetricCard
              label="Asset Download"
              value={
                result.renderStats?.assetDownloadSeconds != null
                  ? `${result.renderStats.assetDownloadSeconds.toFixed(1)}s`
                  : null
              }
            />
            <MetricCard
              label="Convert Proto"
              value={
                result.renderStats?.convertProtoSeconds != null
                  ? `${result.renderStats.convertProtoSeconds.toFixed(1)}s`
                  : null
              }
            />
            <MetricCard
              label="Peak Memory"
              value={
                result.renderStats?.peakMemoryBytes != null
                  ? formatBytes(result.renderStats.peakMemoryBytes)
                  : null
              }
            />
            <MetricCard
              label="Avg Memory"
              value={
                result.renderStats?.avgMemoryBytes != null
                  ? formatBytes(result.renderStats.avgMemoryBytes)
                  : null
              }
            />
          </div>
        </CardContent>
      </Card>

      {result.gpuInfo && (
        <Card>
          <CardHeader>
            <CardTitle>GPU Info</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {result.gpuInfo.version && (
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">Version</div>
                  <a
                    href={`https://github.com/bungeeapp/jerboa/commit/${result.gpuInfo.version}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium mt-1 text-primary hover:underline font-mono block"
                  >
                    {result.gpuInfo.version.slice(0, 12)}
                  </a>
                </div>
              )}
              <MetricCard label="Adapter" value={result.gpuInfo.adapter} />
              <MetricCard label="Backend" value={result.gpuInfo.backend} />
              <MetricCard label="Device Type" value={result.gpuInfo.deviceType} />
              <MetricCard label="Driver" value={result.gpuInfo.driver} />
              <MetricCard
                label="Software Renderer"
                value={result.gpuInfo.isSoftware ? "Yes" : "No"}
              />
              <MetricCard label="MSAA Samples" value={result.gpuInfo.msaaSamples} />
              {result.gpuInfo.diagnostic && (
                <MetricCard label="Diagnostic" value={result.gpuInfo.diagnostic} />
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {result.jerboaStdout && (
        <Card>
          <CardHeader>
            <CardTitle>Renderer Output</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="max-h-96 overflow-auto rounded-md bg-muted p-3 text-xs whitespace-pre-wrap break-words">
              {result.jerboaStdout}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
